import { useUIState } from '@hotjar/webapp/features/Core/hooks/useUIState';
import { usePartialCurrentUser } from '@hotjar/user';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as React from 'react';
import {
  CodeVerificationStep,
  QrCodeStep,
  RecoveryCodesStep,
} from '@hotjar/webapp/features/Mfa/MfaEnforceEnableMfaPage/steps';
import { useQuery } from 'react-query';
import type { GenerateMfaSettingsResponseModel } from '@hotjar/webapp/domains/mfa/model';
import { generateMfaSettings } from '@hotjar/webapp/domains/mfa/api';
import { LandingLayout } from '@hotjar/webapp/components/Landing';
import { useQuery as useQueryString } from '@hotjar/webapp/utils/routing/utils/get-route';

type StepKeys = 'QR_CODE' | 'CODE_VERIFICATION' | 'SECURITY_CODES';

export const MfaEnforceEnableMfaPage = ({ backUrl }: { backUrl: string }): JSX.Element => {
  const { setIsAppInterfaceVisible } = useUIState();
  const { data: user, refetch } = usePartialCurrentUser({ refetchOnWindowFocus: false });
  const [pageStep, setPageStep] = useState<StepKeys>('QR_CODE');
  const navigate = useNavigate();
  const urlQuery = useQueryString();

  const userId = user?.id;

  const {
    data: mfaSettings,
    isLoading: isMfaSettingsLoading,
    status: mfaSettingsStatus,
  } = useQuery<GenerateMfaSettingsResponseModel | undefined>(
    ['mfa-settings', userId],
    () => (userId ? generateMfaSettings(userId) : undefined),
    {
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      enabled: !!userId && pageStep === 'QR_CODE',
    },
  );

  const { uri, recoveryCodes } = mfaSettings ?? {};

  useEffect(() => {
    setIsAppInterfaceVisible(false);
  }, [setIsAppInterfaceVisible]);

  useEffect(() => {
    refetch().then((value) => {
      if (!value.data?.email) {
        navigate('/login');
      }
    });
  }, [navigate, refetch]);

  const onBack = useCallback(() => {
    switch (pageStep) {
      case 'QR_CODE':
      case 'CODE_VERIFICATION':
        setPageStep('QR_CODE');
        break;
      default:
        navigate(backUrl);
        break;
    }
  }, [backUrl, navigate, pageStep]);

  const onNext = useCallback(() => {
    const next = urlQuery.get('next');

    switch (pageStep) {
      case 'QR_CODE':
        setPageStep('CODE_VERIFICATION');
        break;
      case 'CODE_VERIFICATION':
        setPageStep('SECURITY_CODES');
        break;
      case 'SECURITY_CODES':
      default:
        navigate(next || '/');
        break;
    }
  }, [urlQuery, navigate, pageStep]);

  const steps = useMemo<Record<StepKeys, () => ReactNode>>(
    () => ({
      QR_CODE: () => (
        <QrCodeStep
          uri={uri}
          onBack={onBack}
          onNext={onNext}
          hasErrors={mfaSettingsStatus === 'error'}
        />
      ),
      CODE_VERIFICATION: () =>
        userId && <CodeVerificationStep userId={userId} onBack={onBack} onNext={onNext} />,
      SECURITY_CODES: () =>
        recoveryCodes && <RecoveryCodesStep onNext={onNext} recoveryCodes={recoveryCodes} />,
    }),
    [mfaSettingsStatus, onBack, onNext, recoveryCodes, uri, userId],
  );

  return <LandingLayout isLoading={isMfaSettingsLoading}>{steps[pageStep]()}</LandingLayout>;
};
